import type { EnvironmentId, ModelSelection } from "@cleo/contracts";

import { usePrimarySettings } from "../hooks/useSettings";
import {
  resolveRootSessionModel,
  rootSessionModelNeedsCatalog,
  type RootSessionModelResolution,
} from "./defaultSessionModel";
import {
  readRootSessionModelCatalog,
  useRootSessionProviderEntries,
} from "./rootSessionModelSources";
import {
  useEnsureWorldFilesProject,
  useWorldSessionLaunchProjects,
} from "./useWorldSessionLaunchContext";
import { prepareWorldRootSessionExecutionBirth } from "./worldSessionExecutionBirth";
import type { WorldSessionBirthAttemptRecord } from "./worldSessionBirthAttemptsClient";
import type { WorldSessionLaunchProject } from "./worldSessionFirstExecution";
import {
  defaultWorldHomeFolder,
  resolveWorldFilesRoot,
  resolveWorldSessionLaunchGrounding,
  useWorldWorkspaceBindings,
} from "./worldWorkspaceBinding";

/**
 * The one law for a ROOT session's defaults (2026-09-10) — a new room's first
 * session and a new session opened in an existing room alike: the Settings
 * default model (Neucleos Fast until changed) when it can run here (F37,
 * `resolveRootSessionModel`), the World's listed Codebase, else its Default
 * files folder, else the World's home. Tool selection stays empty until the
 * person changes it in the session.
 */
export function useRootSessionDefaults(worldKey: string) {
  const settings = usePrimarySettings();
  const projects = useWorldSessionLaunchProjects();
  const bindingsByEnvironment = useWorldWorkspaceBindings();
  const ensureFilesProject = useEnsureWorldFilesProject();
  const grounding = resolveWorldSessionLaunchGrounding({
    worldKey,
    projects,
    bindingsByEnvironment,
    parentExecution: null,
  });
  const bindingEntries = [...bindingsByEnvironment.entries()];
  const worldBinding = bindingEntries
    .map(([, bindings]) => bindings[worldKey])
    .find((binding) => binding !== undefined);
  const listedCodebase = worldBinding?.sourceFolders?.codebase[0] ?? null;
  const listedFilesFolder =
    worldBinding?.sourceFolders?.files[0] ??
    (worldBinding?.sourceFolders === undefined && bindingEntries.length > 0
      ? resolveWorldFilesRoot({
          worldKey,
          environmentId: bindingEntries[0]![0],
          bindingsByEnvironment,
        })
      : null);
  const environmentId: EnvironmentId | null =
    grounding.kind === "bound"
      ? grounding.project.environmentId
      : grounding.kind === "files-root"
        ? grounding.environmentId
        : (bindingEntries[0]?.[0] ?? null);
  const providerEntries = useRootSessionProviderEntries(environmentId, settings);

  /**
   * The model this root session starts on, read at the moment of birth: the
   * environment's providers as they stand and, only when the start would be
   * on the Neucleos platform, the World's catalog. A project's pick can be
   * checked by the same door. Auto requires a confirmed route; a refusal
   * carries its remedy and a fallback carries the World's reason in a note.
   */
  const resolveModel = async (
    configured: ModelSelection | null = settings.defaultSessionModel ?? null,
  ): Promise<RootSessionModelResolution> => {
    const catalog = rootSessionModelNeedsCatalog({ configured, entries: providerEntries })
      ? await readRootSessionModelCatalog(worldKey)
      : null;
    return resolveRootSessionModel({ configured, entries: providerEntries, catalog, worldKey });
  };

  /** Execution storage only: preparing this project never adds a source binding. */
  const prepareExecutionProject = async (): Promise<WorldSessionLaunchProject | null> => {
    if (environmentId === null) return null;
    // A selected Codebase with no project yet is files-root grounding too.
    // Prepare that exact root before reserving the birth; using the Files
    // fallback here sent the first session to the old World home instead.
    const filesFolder =
      grounding.kind === "files-root"
        ? grounding.filesRoot
        : (listedFilesFolder ?? defaultWorldHomeFolder(null, worldKey));
    return listedCodebase !== null && grounding.kind === "bound"
      ? grounding.project
      : ((await ensureFilesProject({ environmentId, filesRoot: filesFolder, projects })) ??
          (grounding.kind === "bound" ? grounding.project : null));
  };

  /**
   * The execution reservation the first-execution launcher adopts, or null
   * when this device holds no binding for the World (the room births alone
   * and grounds on arrival, where the launcher resolves its model). A
   * refusal names itself and its remedy; nothing is prepared before the
   * model is known to run. `note` says why the start chose another model.
   */
  const prepareExecution = async (input: {
    readonly sessionId: string;
    readonly label: string;
  }): Promise<
    | { ok: true; execution: WorldSessionBirthAttemptRecord | null; note?: string }
    | { ok: false; reason: string }
  > => {
    if (environmentId === null) return { ok: true, execution: null };
    const model = await resolveModel();
    if (model._tag === "Refuse") return { ok: false, reason: `${model.reason} ${model.remedy}` };
    const project = await prepareExecutionProject();
    const prepared = prepareWorldRootSessionExecutionBirth({
      worldKey,
      sessionId: input.sessionId,
      label: input.label,
      project,
      modelSelection: model.selection,
      filesRoot: listedFilesFolder,
    });
    if (prepared._tag !== "Launch")
      return { ok: false, reason: "This device cannot start a session for this World yet." };
    return {
      ok: true,
      execution: {
        worldKey,
        sessionId: input.sessionId,
        environmentId: prepared.environmentId,
        input: prepared.input,
      },
      ...(model.note === undefined ? {} : { note: model.note }),
    };
  };

  return { environmentId, resolveModel, prepareExecution, prepareExecutionProject };
}
